/**
 * EllaKnowledgeConsole.tsx
 * Interface for researching regulations or requesting explanatory memos.
 */

import { useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Loader2, Send, BookOpen } from 'lucide-react';

export default function EllaKnowledgeConsole({ jurisdiction }: { jurisdiction?: string }) {
  const [q, setQ] = useState('');
  const [a, setA] = useState('');
  const [refs, setRefs] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);

  async function ask() {
    if (!q.trim()) return;
    setLoading(true);
    const res = await supabase.functions.invoke('ella-knowledge', {
      body: { question: q, jurisdiction },
    });
    setA(res.data?.answer || 'No response.');
    setRefs(res.data?.refs || []);
    setLoading(false);
  }

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !loading) {
      ask();
    }
  };

  return (
    <Card className="border-border bg-card">
      <CardHeader>
        <div className="flex items-center gap-2">
          <BookOpen className="h-5 w-5 text-primary" />
          <CardTitle>Ella Knowledge Mode</CardTitle>
        </div>
        <p className="text-sm text-muted-foreground">
          Research regulations and get explanatory guidance
        </p>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="flex gap-2">
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder="Ask Ella about any regulation..."
            disabled={loading}
          />
          <Button onClick={ask} disabled={loading} size="icon">
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
          </Button>
        </div>
        {a && <div className="bg-muted p-4 rounded-md text-sm whitespace-pre-wrap">{a}</div>}
        {refs.length > 0 && (
          <div className="border-t pt-3">
            <p className="text-xs font-medium text-muted-foreground mb-2">
              Sources ({refs.length}):
            </p>
            <ul className="list-disc pl-5 text-xs text-muted-foreground space-y-1">
              {refs.map((r: any) => (
                <li key={r.id}>{r.title}</li>
              ))}
            </ul>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
